/**
 * The banks that draw on a bank through a bank overlay, as monitor-view reports
 * them.
 *
 * The card's own bank comes from the single-bank endpoint, which does not list
 * them; monitor-view does, on the target bank (`overlay_sources`), including a
 * hidden source bank the viewer could not otherwise see. The dashboard has
 * already fetched monitor-view for its bank selector, so this reads RTK Query's
 * cache rather than fetching again — every fulfilled monitor-view entry, so it
 * does not depend on the argument the selector happened to fetch it with.
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import { BankKey, bankKey } from "../../../utils/typedKeys";
import { stringToUuidT } from "../../../utils/uuid";

/** One bank drawing on another, as monitor-view names it. */
export interface OverlaySource {
  id: string;
  name: string;
  /** `id` keyed the way lift state and `bankConnected` key banks. */
  key: BankKey;
}

interface MonitorViewBank {
  id?: string;
  overlay_sources?: { id: string; name: string }[];
}

interface MonitorViewOrg {
  servers?: { channels?: { banks?: MonitorViewBank[] }[] }[];
}

const NO_SOURCES: OverlaySource[] = [];

/**
 * The organizations of a cached monitor-view response. The endpoint answers
 * `{data: [org, ...]}`, and the cascader reads `.data`; a bare array is
 * accepted too.
 */
function orgsOf(view: unknown): MonitorViewOrg[] {
  if (Array.isArray(view)) return view as MonitorViewOrg[];
  const data = (view as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(data) ? (data as MonitorViewOrg[]) : [];
}

/** Every bank in a monitor-view response, flattened. */
function banksOf(view: unknown): MonitorViewBank[] {
  return orgsOf(view).flatMap((org) =>
    (org.servers ?? []).flatMap((server) =>
      (server.channels ?? []).flatMap((channel) => channel.banks ?? []),
    ),
  );
}

/** The cached monitor-view responses, newest last. */
function selectMonitorViews(state: RootState): unknown[] {
  const queries = state.monitoringAPI?.queries ?? {};
  return Object.values(queries)
    .filter(
      (query) => query?.endpointName === "getOrganizationsCascader" && query.status === "fulfilled",
    )
    .map((query) => query?.data);
}

/**
 * The banks drawing on `bankId` (a uuid string), or an empty list when none do
 * or monitor-view has not been fetched.
 */
export default function useOverlaySources(bankId: string | null | undefined): OverlaySource[] {
  const views = useSelector(
    selectMonitorViews,
    (a, b) => a.length === b.length && a.every((view, i) => view === b[i]),
  );
  return useMemo(() => {
    if (!bankId) return NO_SOURCES;
    const wanted = bankId.toLowerCase();
    const bank = views.flatMap(banksOf).find((candidate) => candidate.id?.toLowerCase() === wanted);
    const sources = bank?.overlay_sources ?? [];
    if (sources.length === 0) return NO_SOURCES;
    return sources.map((source) => ({ ...source, key: bankKey(stringToUuidT(source.id)) }));
  }, [bankId, views]);
}
